import React from 'react';
import { Button, Select, TextInput, MultiSelect, ActionIcon, Tooltip } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons';
import type { ActionProps, ValueSelectorProps, ValueEditorProps } from 'react-querybuilder';

// Add Rule / Add Group button (RQB v6 keys: addRuleAction / addGroupAction)
const AddActionElement: React.FC<ActionProps> = ({ label, className, handleOnClick, disabled }) => {
  const text = typeof label === 'string' ? label.replace(/^\+\s*/, '') : label;
  return (
    <Button
      size="xs"
      h={28}
      variant="light"
      color="indigo"
      leftIcon={<IconPlus size={14} />}
      onClick={handleOnClick}
      disabled={disabled}
      className={className}
    >
      {text}
    </Button>
  );
};

// Remove Rule / Remove Group button (RQB v6 keys: removeRuleAction / removeGroupAction)
const RemoveActionElement: React.FC<ActionProps> = ({
  className,
  handleOnClick,
  disabled,
  title,
}) => (
  <Tooltip label={title || 'Remove'} withArrow>
    <ActionIcon
      size="md"
      variant="subtle"
      color="red"
      onClick={handleOnClick}
      disabled={disabled}
      className={className}
      aria-label={title || 'Remove'}
    >
      <IconTrash size={16} />
    </ActionIcon>
  </Tooltip>
);

// Shared selector, used for field / operator / combinator selectors and for value-as-select.
// RQB option lists use `name` (field/operator/combinator) or `value` (some value lists),
// so accept both. `clearable` is opt-in — field/operator/combinator must never be null.
const ValueSelector: React.FC<ValueSelectorProps & { clearable?: boolean }> = ({
  value,
  options = [],
  multiple,
  disabled,
  className,
  handleOnChange,
  clearable = false,
}) => {
  const selectOptions = options.flatMap((option: any) => {
    if (typeof option === 'string') {
      return [{ value: option, label: option }];
    }
    // Option group (e.g. { label, options: [...] }) — flatten one level.
    if (Array.isArray(option?.options)) {
      return option.options.map((o: any) => ({
        value: o.value ?? o.name,
        label: o.label ?? o.value ?? o.name,
      }));
    }
    const val = option?.value ?? option?.name;
    return [{ value: val, label: option?.label ?? val }];
  });

  if (multiple) {
    return (
      <MultiSelect
        value={Array.isArray(value) ? value : []}
        onChange={handleOnChange}
        data={selectOptions}
        disabled={disabled}
        size="xs"
        className={className}
        clearable
      />
    );
  }

  return (
    <Select
      value={(value as string) ?? ''}
      onChange={(v) => handleOnChange(v ?? '')}
      data={selectOptions}
      disabled={disabled}
      size="xs"
      className={className}
      clearable={clearable}
    />
  );
};

// Custom Value Editor
const ValueEditor: React.FC<ValueEditorProps> = ({
  value,
  handleOnChange,
  inputType,
  values,
  disabled,
  className,
  path,
  level,
  schema,
}) => {
  // NOTE: react-querybuilder's default `getValues` returns `[]` (never undefined) when a
  // field has no predefined options, and `[]` is truthy — so a bare `if (values)` here
  // rendered an empty <Select> for every free-text field, making the value un-typeable.
  // Only use the selector when there are actual options to choose from.
  if (Array.isArray(values) && values.length > 0) {
    return (
      <ValueSelector
        value={value}
        options={values}
        handleOnChange={handleOnChange}
        disabled={disabled}
        className={className}
        path={path}
        level={level}
        schema={schema}
        clearable
      />
    );
  }

  return (
    <TextInput
      value={value || ''}
      onChange={(e) => handleOnChange(e.currentTarget.value)}
      type={inputType || 'text'}
      disabled={disabled}
      size="xs"
      className={className}
    />
  );
};

export const MantineQueryBuilderComponents = {
  addRuleAction: AddActionElement,
  addGroupAction: AddActionElement,
  removeRuleAction: RemoveActionElement,
  removeGroupAction: RemoveActionElement,
  combinatorSelector: ValueSelector,
  fieldSelector: ValueSelector,
  operatorSelector: ValueSelector,
  valueEditor: ValueEditor,
};
